import { useAuth } from '../../lib/auth';
import { scr } from '../../lib/api';
import type { MyPaymentLine, RegistryProfile } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, PageHeader, Spinner, StatusBadge } from '../../ui/components';

/** S06 now exposes GET /api/v1/payments/me — real per-beneficiary pay lines
 * from every run you were included in, not just the registry pay method. */
export function PaymentHistory() {
  const { persona } = useAuth();
  const profileQ = useApiQuery<RegistryProfile>(persona?.nin ? `/api/v1/registry/profile/${persona.nin}` : null);
  const paymentsQ = useApiQuery<{ items: MyPaymentLine[] }>('/api/v1/payments/me');
  const beneficiary = profileQ.data?.beneficiary;
  const lines = paymentsQ.data?.items ?? [];

  return (
    <div>
      <PageHeader title="Payment History" subtitle="How you're paid, and where to get a statement" />

      {profileQ.loading ? <Spinner label="Loading…" /> : null}
      {profileQ.error ? <ErrorNote message={profileQ.error} /> : null}

      {beneficiary ? (
        <div className="card mb-6 px-4 py-3.5">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Your payment method on file</div>
          <div className="mt-1 text-sm font-semibold text-slate-900">
            {beneficiary.payMethod === 'BANK' ? 'Bank transfer' : 'District cash office'}
          </div>
          {beneficiary.payMethod === 'BANK' ? (
            <div className="text-xs text-slate-500">Account {beneficiary.bankAccount ?? 'not on file — visit your district office'}</div>
          ) : (
            <div className="text-xs text-slate-500">Collect in person at your district ASP cash office</div>
          )}
        </div>
      ) : !profileQ.loading ? (
        <div className="mb-6">
          <EmptyState title="You are not yet in the social registry" hint="A payment method is set up once you register at a district ASP office." />
        </div>
      ) : null}

      <div className="card overflow-x-auto">
        {paymentsQ.loading ? (
          <Spinner label="Loading your payment history…" />
        ) : paymentsQ.error ? (
          <ErrorNote message={paymentsQ.error} />
        ) : lines.length === 0 ? (
          <EmptyState
            title="No payments yet"
            hint="Payments appear here once a monthly benefit pay run that includes you has been sent."
          />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Period</th>
                <th className="table-head">Benefit</th>
                <th className="table-head">Amount</th>
                <th className="table-head">Kind</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.id}>
                  <td className="table-cell font-medium">{l.run.period}</td>
                  <td className="table-cell">{l.benefitCode}</td>
                  <td className="table-cell">{scr(l.amount)}</td>
                  <td className="table-cell text-slate-500">{l.payKind === 'RECURRING' ? 'Recurring' : 'One-off'}</td>
                  <td className="table-cell">
                    <StatusBadge status={l.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <p className="mt-3 text-xs text-slate-400">
        For older statements or a payment that doesn't look right, contact your district ASP office or the ASP
        Finance Division.
      </p>
    </div>
  );
}
